/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { REFERENCE_KWH, earningsFromEres, ereCountFor } from './earnings';

export interface ExampleVars {
  /** Rekenwaarde — the single price every calculation uses. */
  ere_price_eur: number;
  /** Displayed market band around the rekenwaarde (indicative, not used in calcs). */
  ere_price_low_eur: number;
  ere_price_high_eur: number;
  market_fee_pct: number;
  /**
   * Whole EREs of the reference household — DERIVED from REFERENCE_KWH
   * (src/lib/earnings.ts), never read from the CMS. The two CMS counts (1 500
   * and 25 000, frozen per environment by i18n masking) drifted away from the
   * reference volume the comparison calculates with, so the price page and the
   * kennisbank printed a different household than the comparer next to them.
   * The fields are kept on the type because older saves still carry them.
   */
  private_eres_per_year: number;
  private_commission_pct: number;
  business_eres_per_year: number;
  business_commission_pct: number;
}

export interface ExampleResult {
  gross: number;
  market: number;
  commission: number;
  net: number;
}

export const EXAMPLE_VARS_DEFAULTS: ExampleVars = {
  // €/ERE (1 ERE = 1 kg CO2). Mirrors ERE_PRICE in earnings.ts — the fallback
  // for an environment without a price series; the live reading is overlaid
  // per request by src/lib/erePrice.js.
  ere_price_eur: 0.49,
  ere_price_low_eur: 0.45,
  ere_price_high_eur: 0.51,
  market_fee_pct: 1.5,
  private_eres_per_year: ereCountFor(REFERENCE_KWH.private),
  private_commission_pct: 20,
  business_eres_per_year: ereCountFor(REFERENCE_KWH.business),
  business_commission_pct: 15,
};

const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Tolerant of partial / loose CMS payloads (deep-merge round-trips, missing
// keys, string-typed numbers from older saves) — coerces each field with the
// matching default so the page never blanks out.
export function normalizeExampleVars(raw: unknown): ExampleVars {
  const r = (raw ?? {}) as Partial<Record<keyof ExampleVars, unknown>>;
  const price = num(r.ere_price_eur, EXAMPLE_VARS_DEFAULTS.ere_price_eur);
  // Band defaults straddle the price; a band that doesn't contain the price
  // (stale CMS low/high after a price edit) is clamped so the card never
  // shows "€0,45 – €0,51" next to a rekenwaarde of €0,60.
  const low = Math.min(num(r.ere_price_low_eur, EXAMPLE_VARS_DEFAULTS.ere_price_low_eur), price);
  const high = Math.max(num(r.ere_price_high_eur, EXAMPLE_VARS_DEFAULTS.ere_price_high_eur), price);
  return {
    ere_price_eur: price,
    ere_price_low_eur: low,
    ere_price_high_eur: high,
    market_fee_pct: num(r.market_fee_pct, EXAMPLE_VARS_DEFAULTS.market_fee_pct),
    // Whatever a CMS file still carries, the reference household wins — this
    // is what defeats the 1 500 / 25 000 frozen in i18n_admin on acc and prd.
    private_eres_per_year: ereCountFor(REFERENCE_KWH.private),
    private_commission_pct: num(
      r.private_commission_pct,
      EXAMPLE_VARS_DEFAULTS.private_commission_pct
    ),
    business_eres_per_year: ereCountFor(REFERENCE_KWH.business),
    business_commission_pct: num(
      r.business_commission_pct,
      EXAMPLE_VARS_DEFAULTS.business_commission_pct
    ),
  };
}

/**
 * The four lines of a rekentabel — the same cent-exact chain the comparison
 * card and the provider page use (src/lib/earnings.ts), so a table's
 * "832 ERE × € 0,445 = € 370,24" reproduces from its own printed operands.
 */
export function computeExample(input: {
  erePriceEur: number;
  eresPerYear: number;
  marketFeePct: number;
  commissionPct: number;
}): ExampleResult {
  const { gross, market, commission, net } = earningsFromEres(
    input.eresPerYear,
    input.erePriceEur,
    input.commissionPct,
    0,
    input.marketFeePct
  );
  return { gross, market, commission, net };
}
